import {
  DealType,
  IConfiguration,
  LocationType,
  Maybe,
  StorageTime
} from 'src/types/graphql'
import { getEffectiveDealType } from 'src/utils/pricing'

// ---------------------------------------------------------------------------
// Verticals
// Aligned with backend SIMPLIFIED_LOCATION_TYPE (spotme-server/src/constants)
// ---------------------------------------------------------------------------

export type Vertical = 'storage' | 'parking' | 'other'

const STORAGE_TYPES: LocationType[] = [
  // Primary storage types
  LocationType.SelfStorageUnit,
  LocationType.Warehouse,
  LocationType.BodegaCommercial,
  LocationType.BodegaIndustrial,
  LocationType.NaveIndustrial,
  LocationType.MiniWinery,
  LocationType.ShippingContainer,
  // Backend normalizes these → SelfStorageUnit
  LocationType.Room,
  LocationType.Local,
  LocationType.Yard,
  LocationType.Terrace,
  LocationType.Shed,
  LocationType.Closet,
  LocationType.Basement,
  LocationType.Attic,
  LocationType.House,
  LocationType.Penthouse,
  LocationType.Other,
  // Backend normalizes Office → Warehouse
  LocationType.Office
]

const PARKING_TYPES: LocationType[] = [
  LocationType.Parking,
  LocationType.Garage,
  LocationType.Land
]

export const getVertical = (
  locationType: Maybe<LocationType> | undefined
): Vertical => {
  if (!locationType) return 'other'
  if (PARKING_TYPES.includes(locationType)) return 'parking'
  if (STORAGE_TYPES.includes(locationType)) return 'storage'
  return 'other'
}

/**
 * Pillar-slug → vertical fallback. Used when an event fires from a pillar
 * surface (B2B specialist CTA on /renta-bodegas-comerciales,
 * /renta-naves-industriales, Almacenamiento a Domicilio, etc.) and no
 * configuration / locationType is attached to the lead. Lets PostHog and
 * Google Ads slice campaign goals by vertical even without an inventory hit.
 */
export const getVerticalFromPillar = (
  pillar: string | null | undefined
): Vertical => {
  switch (pillar) {
    case 'renta-mini-bodegas':
    case 'renta-bodegas-comerciales':
    case 'renta-naves-industriales':
    case 'storage-ondemand':
      return 'storage'
    case 'pension-estacionamiento':
      return 'parking'
    default:
      return 'other'
  }
}

// ---------------------------------------------------------------------------
// Product type
// ---------------------------------------------------------------------------

export type ProductType =
  | 'mini_bodega'
  | 'parking_monthly'
  | 'parking_b2b'
  | 'bodega_comercial'
  | 'bodega_industrial'
  | 'nave_industrial'
  | 'other'

export const getProductType = (
  locationType: Maybe<LocationType> | undefined,
  dealType?: Maybe<DealType>
): ProductType => {
  switch (locationType) {
    // Backend: SelfStorageUnit + all types normalized to it
    case LocationType.SelfStorageUnit:
    case LocationType.MiniWinery:
    case LocationType.ShippingContainer:
    case LocationType.Room:
    case LocationType.Local:
    case LocationType.Yard:
    case LocationType.Terrace:
    case LocationType.Shed:
    case LocationType.Closet:
    case LocationType.Basement:
    case LocationType.Attic:
    case LocationType.House:
    case LocationType.Penthouse:
    case LocationType.Other:
      return 'mini_bodega'
    // Backend: Parking + Garage + Land
    case LocationType.Parking:
    case LocationType.Garage:
    case LocationType.Land:
      return dealType === DealType.Broker ? 'parking_b2b' : 'parking_monthly'
    // Backend: Warehouse category
    case LocationType.BodegaCommercial:
    case LocationType.Warehouse:
    case LocationType.Office:
      return 'bodega_comercial'
    case LocationType.BodegaIndustrial:
      return 'bodega_industrial'
    case LocationType.NaveIndustrial:
      return 'nave_industrial'
    default:
      return 'other'
  }
}

// ---------------------------------------------------------------------------
// Analytics customer type
// ---------------------------------------------------------------------------

export type AnalyticsCustomerType = 'b2c' | 'sme' | 'b2b'

// PRICE_THRESHOLD comes from `src/utils/pricing.ts` (single source of truth,
// mirrored byte-for-byte from the server's `PRICE_THRESHOLD` constant). Do
// NOT shadow it here — keeping a local copy is how analytics enrichment
// previously drifted from listing-page rendering after a server-side
// threshold tweak.
const PRO_MIN_PARKING_UNITS = 3

// ---------------------------------------------------------------------------
// Analytics deal type — the three core SpotMe funnels
//
// monthly     → SpotMe Now (on-platform checkout → purchase)
// broker      → SpotMe Pro (specialist-facilitated → generate_lead / broker_lead)
// traditional → Anexo C broker-mediated (WhatsApp handoff → generate_lead / traditional_lead)
//
// Emitted on every event via buildEnrichment so campaigns can slice any
// funnel step by deal_type without reconstructing it from customer_type +
// product_type. Flotillas (fleet parking) stay 'monthly' — fleet quantity
// does NOT promote a listing to broker here.
// ---------------------------------------------------------------------------

export type AnalyticsDealType = 'monthly' | 'broker' | 'traditional'

export const getAnalyticsDealType = (
  dealType?: Maybe<DealType>,
  monthlyPrice?: number
): AnalyticsDealType => {
  // Single source of truth: `getEffectiveDealType` in `src/utils/pricing.ts`.
  // Applies the same null-as-Monthly + price>PRICE_THRESHOLD → Broker
  // heuristic the listing-page renderer and the server's `resolveDealType`
  // use, so this analytics bucket never disagrees with the funnel.
  const effective = getEffectiveDealType({
    dealType: dealType ?? null,
    price: monthlyPrice ?? null
  })
  if (effective === DealType.Traditional) return 'traditional'
  if (effective === DealType.Broker) return 'broker'
  return 'monthly'
}

/**
 * Slug used in segmented event NAMES (e.g. `contact_wa_storage_now`).
 * `monthly` → `now` keeps event names aligned with marketing vocabulary
 * ("SpotMe Now"); `broker` and `traditional` are unchanged.
 */
export type DealTypeSlug = 'now' | 'broker' | 'traditional'

export const getDealTypeSlug = (
  dealType?: Maybe<DealType>,
  monthlyPrice?: number
): DealTypeSlug => {
  const analytics = getAnalyticsDealType(dealType, monthlyPrice)
  return analytics === 'monthly' ? 'now' : analytics
}

// ---------------------------------------------------------------------------
// Analytics location type — lowercased enum value, single source of truth.
//
// Why: historical PostHog firing sites split into three flavors —
//   1. UPPERCASE enum (`SELF_STORAGE_UNIT`) — `lead_form_step`, `Calculate`
//   2. Spanish display label (`Mini-Bodega`) — `add_to_cart_intent`
//   3. lowercased enum (`self_storage_unit`) — this helper
// Display labels are also locale-dependent, so the same listing produced
// different `location_type` values per user. Always pipe through this
// helper before sending to PostHog / GA4 / Meta.
// ---------------------------------------------------------------------------

export type AnalyticsLocationType = Lowercase<`${LocationType}`>

export const getAnalyticsLocationType = (
  locationType?: Maybe<LocationType>
): AnalyticsLocationType | undefined => {
  if (!locationType) return undefined
  return locationType.toLowerCase() as AnalyticsLocationType
}

export const getAnalyticsCustomerType = (opts: {
  dealType?: Maybe<DealType>
  monthlyPrice?: number
  quantity?: number
  locationType?: Maybe<LocationType>
}): AnalyticsCustomerType => {
  const { dealType, monthlyPrice, quantity, locationType } = opts
  // Route through the canonical resolver — same null-as-Monthly +
  // price>PRICE_THRESHOLD → Broker semantics as the listing-page surface
  // and the server's `resolveDealType`.
  const effective = getEffectiveDealType({
    dealType: dealType ?? null,
    price: monthlyPrice ?? null
  })
  if (effective === DealType.Broker || effective === DealType.Traditional)
    return 'b2b'
  if (
    PARKING_TYPES.includes(locationType as LocationType) &&
    quantity &&
    quantity >= PRO_MIN_PARKING_UNITS
  )
    return 'b2b'
  if (
    locationType === LocationType.NaveIndustrial ||
    locationType === LocationType.BodegaIndustrial
  )
    return 'sme'
  return 'b2c'
}

// ---------------------------------------------------------------------------
// Duration bucket — aligned with business value tiers
// ---------------------------------------------------------------------------

/** short = <3 months, mid = 3–11 months, long = 12+ months */
export type DurationBucket = 'short' | 'mid' | 'long'

export const getDurationBucket = (
  storageTime?: Maybe<StorageTime> | ''
): DurationBucket => {
  switch (storageTime) {
    case StorageTime.Month:
    case StorageTime.FewMonths:
      return 'short'
    case StorageTime.FewMonthsSix:
    case StorageTime.FewMonthsTwelve:
      return 'mid'
    case StorageTime.Year:
    case StorageTime.FewYears:
    case StorageTime.Indefinitely:
      return 'long'
    default:
      return 'short'
  }
}

// ---------------------------------------------------------------------------
// Storage time → months
// Based on backend STORAGE_TIME_MONTHS, capped at 12 for ad platform sanity.
// Backend uses raw values (24, 36) for HubSpot deal sizing; frontend caps
// because storageTime is aspirational — renters cancel early, so we stay
// conservative to keep tROAS stable.
// ---------------------------------------------------------------------------

const MAX_MONTHS = 12
/** Conservative fallback when user hasn't selected a storageTime yet */
const DEFAULT_MONTHS = 3

/** Expected months for a StorageTime value. Capped at 12. */
export const getMonthsFromStorageTime = (
  storageTime?: Maybe<StorageTime> | ''
): number => {
  switch (storageTime) {
    case StorageTime.Month:
      return 1
    case StorageTime.FewMonths:
      return 2
    case StorageTime.FewMonthsSix:
      return 4.5
    case StorageTime.FewMonthsTwelve:
      return 9
    case StorageTime.Year:
    case StorageTime.FewYears:
    case StorageTime.Indefinitely:
      return MAX_MONTHS
    default:
      return DEFAULT_MONTHS
  }
}

// ---------------------------------------------------------------------------
// Total Deal Value (TDV)
// TDV = monthlyPrice × quantity × √months
//
// Duration is dampened via square root because it's the least reliable signal:
// price and quantity are contractual, but storageTime is aspirational (renters
// cancel early). √months still rewards long-term intent — a 12-month renter
// gets ~3.5× the value of a 1-month renter — without letting duration
// dominate the value signal and destabilize tROAS.
// ---------------------------------------------------------------------------

/**
 * Total Deal Value for ad platform value-based bidding (tROAS).
 * When storageTime is not set, defaults to 3 months (√3 ≈ 1.73×).
 */
export const getTotalDealValue = (
  monthlyPrice: number,
  quantity?: number,
  storageTime?: Maybe<StorageTime> | ''
): number => {
  const months = getMonthsFromStorageTime(storageTime)
  const qty = quantity ?? 1
  const monthlyValue = monthlyPrice * qty
  // Dampen duration via √months so price × qty stays dominant
  if (months > 0 && monthlyValue > 0) return monthlyValue * Math.sqrt(months)
  return monthlyValue
}

// ---------------------------------------------------------------------------
// Inventory priority
// ---------------------------------------------------------------------------

export type InventoryPriority = 'high' | 'medium' | 'low'

export const getInventoryPriority = (
  locationType: Maybe<LocationType> | undefined,
  dealType?: Maybe<DealType>
): InventoryPriority => {
  if (
    locationType === LocationType.SelfStorageUnit ||
    locationType === LocationType.MiniWinery
  )
    return 'high'
  if (
    PARKING_TYPES.includes(locationType as LocationType) &&
    dealType === DealType.Broker
  )
    return 'high'
  if (PARKING_TYPES.includes(locationType as LocationType)) return 'medium'
  if (
    locationType === LocationType.BodegaCommercial ||
    locationType === LocationType.Warehouse
  )
    return 'medium'
  return 'low'
}

// ---------------------------------------------------------------------------
// Enrichment payload builder
// ---------------------------------------------------------------------------

export type EnrichedEventParams = {
  vertical: Vertical
  customer_type: AnalyticsCustomerType
  deal_type: AnalyticsDealType
  product_type: ProductType
  inventory_priority: InventoryPriority
  duration_bucket?: DurationBucket
  total_deal_value?: number
  requested_months?: number
}

/** Build the enrichment fields from a configuration + optional context. */
export const buildEnrichment = (
  config: Maybe<IConfiguration> | undefined,
  opts?: {
    storageTime?: Maybe<StorageTime> | ''
    quantity?: number
    monthlyPrice?: number
    /**
     * Sub-event hint for listing-less surfaces. Currently:
     *   `on_demand` → vertical=storage, product_type=mini_bodega,
     *                 deal_type=traditional, customer_type=b2c.
     * Aligned with the backend marketing classifier — Almacenamiento a
     * Domicilio leads land in the Traditional funnel.
     */
    subType?: 'on_demand'
    /**
     * Pillar slug fallback. Used when the event fires from a B2B pillar
     * surface (broker_contact / pillar_specialist) without a listing
     * config — lets vertical/product_type still resolve from the pillar.
     */
    pillar?: string | null
  }
): EnrichedEventParams => {
  const locationType = config?.locationType as LocationType | undefined
  const rawDealType = config?.dealType
  const price = opts?.monthlyPrice ?? config?.price ?? 0
  const quantity = opts?.quantity ?? 1
  const storageTime = opts?.storageTime

  // Listing-less on_demand lead form: there's no config to derive from, so
  // hard-code the taxonomy that matches the backend's Traditional funnel
  // routing.
  if (opts?.subType === 'on_demand') {
    return {
      vertical: 'storage',
      customer_type: 'b2c',
      deal_type: 'traditional',
      product_type: 'mini_bodega',
      inventory_priority: 'high',
      duration_bucket: storageTime ? getDurationBucket(storageTime) : undefined,
      total_deal_value: undefined,
      requested_months: storageTime
        ? getMonthsFromStorageTime(storageTime)
        : undefined
    }
  }

  // Resolve once via the canonical helper. Passing `pillarPage` opts into
  // the B2B pillar promotion so a sub-$30k bodega/nave listing emits
  // `deal_type='broker'` and its segmented events land on broker_* names
  // that are excluded from TIER_ROLLUP_MAP — preventing storage_secondary
  // pollution. Reading B: explicit dealType wins, so a host-tagged Monthly
  // bodega stays Monthly.
  const effectiveDealType = getEffectiveDealType({
    dealType: rawDealType ?? null,
    price,
    pillarPage: config?.pillarPage
  })

  const verticalFromType = getVertical(locationType)
  const vertical: Vertical =
    verticalFromType === 'other' && opts?.pillar
      ? getVerticalFromPillar(opts.pillar)
      : verticalFromType
  const product_type = getProductType(locationType, effectiveDealType)
  const customer_type = getAnalyticsCustomerType({
    dealType: effectiveDealType,
    monthlyPrice: price * quantity,
    quantity,
    locationType
  })
  const deal_type = getAnalyticsDealType(effectiveDealType, price)
  const inventory_priority = getInventoryPriority(
    locationType,
    effectiveDealType
  )
  const duration_bucket = storageTime
    ? getDurationBucket(storageTime)
    : undefined
  const requested_months = storageTime
    ? getMonthsFromStorageTime(storageTime)
    : undefined
  const total_deal_value = price
    ? getTotalDealValue(price, quantity, storageTime)
    : undefined

  return {
    vertical,
    customer_type,
    deal_type,
    product_type,
    inventory_priority,
    duration_bucket,
    total_deal_value,
    requested_months
  }
}
